import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  BankAccount,
  BankAccountEditReason,
  BankStatementMonthlySummary,
  NewBankAccount
} from 'app/models/dto/bank-account.model';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { AddBankAccount, CloneBankAccount, CreateBankStatementMonthlySummary, EditBankAccount, EditBankAccountAndSummaries, EditBankStatementMonthlySummaries, EditBankStatementMonthlySummariesReason, GetBankAccountValues, RemoveBankAccount, PullSummariesSync } from 'app/store/bank-account/bank-account.actions';
import { BankAccountState } from 'app/store/bank-account/bank-account.state';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';
import { BankAccountApiPayload } from 'app/services/bank-account.service';
import { DocumentState } from 'app/store/documents/document.state';
import { DocumentLinkInfo } from 'app/models/dto/document.model';
import { MatDialog } from '@angular/material/dialog';
import { BankAccountAddDialogComponent } from '../bank-account-add-dialog/bank-account-add-dialog.component';

@Component({
  selector: 'app-monthly-totals-smart',
  templateUrl: './monthly-totals-smart.component.html',
  styleUrls: ['./monthly-totals-smart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MonthlyTotalsSmartComponent implements OnInit {

  @Select(BankAccountState.cloning) cloning$: Observable<boolean>;
  @Input() borrowerId: number;
  @Input() showEmptyMessage: boolean;
  @Input() newFdTableView?: boolean = false;
  @Input() readOnly?: boolean = false;

  bankValues$: Observable<BorrowerValue[] | undefined>;
  bankAccounts$: Observable<BankAccount[] | undefined>;
  bankAccountsStatus$: Observable<BankAccountApiPayload | undefined>;
  documentLinkInfo$: Observable<DocumentLinkInfo>;
  activeTab = 0;
  addingAccount = false;
  editingBankAccountId: number;
  alert: Alert | null;
  startCloning: boolean;
  accounts: BankAccount[] | undefined;
  originalAccount: BankAccount | undefined;

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    setTimeout(() => { // added to avoid weird timing with the Funding Desk finances tab
      this.store.dispatch(new GetBankAccountValues(this.borrowerId));
    });
    this.bankValues$ = this.store.select(BankAccountState.bankValues).pipe(map(x => x(this.borrowerId)));
    this.bankAccounts$ = this.store.select(BankAccountState.bankAccounts).pipe(map(x => x(this.borrowerId)));
    this.bankAccountsStatus$ = this.store.select(BankAccountState.bankAccountsStatus).pipe(map(x => x(this.borrowerId)));
    this.documentLinkInfo$ = this.store.select(DocumentState.documentLinkInfo).pipe(map(x => x()));
    this.bankAccounts$.subscribe(accts => this.accounts = accts);
  }

  getOriginalAccount(account): BankAccount | undefined {
    if (account.parentAccountId) {
      return this.accounts?.find(acct => acct.id === account.parentAccountId);
    }
  }

  onAddAccount(newBankAccount: NewBankAccount) {
    return this.store
      .dispatch(new AddBankAccount(newBankAccount))
      .pipe(
        tap(() => {
          this.addingAccount = false;
          this.activeTab = this.accounts ? this.accounts.length : 0;
          this.cdRef.markForCheck();
        })
      )
      .subscribe();
  }

  onSaveAccount(bankAccount: BankAccount) {
    this.store.dispatch(new EditBankAccount(bankAccount, this.borrowerId))
      .subscribe((data: any) => {
        this.editingBankAccountId = 0;
        this.alert = data.bankAccount.alert;
      });
  }


  onEditAccount(bankAccount: BankAccount) {
    const aggregator = this.accounts?.find(acct => acct.id === bankAccount.id)?.aggregatorName;
    if (aggregator) {
      this.startCloning = true;
      this.store.dispatch(new CloneBankAccount(bankAccount.id, this.borrowerId)).pipe(
        tap(() => {
          const clonedAcct = this.store.selectSnapshot(state => state.bankAccount.clonedAccount);
          this.editingBankAccountId = clonedAcct.id;
          this.activeTab = this.accounts ? this.accounts.length : 0;
          this.startCloning = false;
          this.cdRef.markForCheck();
        })
      ).subscribe({
        error: () => {
          this.alert = this.store.selectSnapshot(state => state.bankAccount.alert);
          this.cdRef.markForCheck();
        }
      });
    } else {
      this.editingBankAccountId = bankAccount.id;
    }
  }

  onCancelEdit(): void {
    this.editingBankAccountId = 0;
  }

  onEditSummaries(summaries: BankStatementMonthlySummary[]) {
    this.store.dispatch(new EditBankStatementMonthlySummaries(summaries, this.borrowerId));
  }

  onEditSummariesReason(bankAccountEditReason: BankAccountEditReason) {
    this.store.dispatch(new EditBankStatementMonthlySummariesReason(this.borrowerId, bankAccountEditReason));
  }

  onEditAccountAndSummaries(bankAccount: BankAccount) {
    this.store.dispatch(new EditBankAccountAndSummaries(bankAccount, this.borrowerId));
  }

  onCreateMonthlySummary(summary: BankStatementMonthlySummary) {
    this.store.dispatch(new CreateBankStatementMonthlySummary(summary, this.borrowerId));
  }

  onRemoveAccount(bankAccount: BankAccount) {
    this.store.dispatch(new RemoveBankAccount(bankAccount.id, this.borrowerId));
  }

  showAccountAddDialog(): void {
    this.dialog.open(BankAccountAddDialogComponent, {
      data: {
        borrowerId: this.borrowerId,
        bankAccounts: this.bankAccounts$,
       },
      width: '500px',
      panelClass: 'add-account-dialog-container'
    })
    .afterClosed().subscribe((newBankAccount: NewBankAccount) => {
      if (newBankAccount) {
        this.onAddAccount(newBankAccount);
      }
    });
  }

  onPullSummariesSync(): void {
    this.store.dispatch(new PullSummariesSync(this.borrowerId));
  }
}
